import { useCallback, useEffect, useMemo, useState } from 'react';
import { supabase } from '../supabase';
import { useActiveOrg } from '../activeOrg';
import {
  isTaskStatusKey,
  seedRow,
  sortTaskStatusVersions,
  TASK_STATUS_KEYS,
  type TaskStatusInstruction,
  type TaskStatusInstructionVersion,
  type TaskStatusKey
} from '../models/taskStatuses';
import { emptyRule, sortRoles, type FunctionRole } from '../models/functionRoles';

export type SettingsTaskStatusesStatus =
  | 'loading'
  | 'ready'
  | 'unauth'
  | 'empty'
  | 'error';

export type ViewerRoleSystemKey = 'owner' | 'admin' | 'member' | null;

/** Working copy of a status row's editable fields. */
export interface TaskStatusDraft {
  description: string;
  instructionMd: string;
}

/** A function role shown as a per-status override slot. */
export interface OverrideRole {
  id: string;
  key: string;
  label: string;
  kind: 'system' | 'custom';
}

/** The live merged Layer 1+2+3 agent context for one status, as an agent
 *  handling a task at that status would receive it. */
export interface MergedPreview {
  /** Layer 1 — the org-standards prose (empty when none or archived). */
  layer1: string;
  /** Layer 2 — the role-rule for the viewer's function role (empty when none,
   *  archived, or the viewer has no function role). */
  layer2: string;
  /** The viewer's function role label for the Layer 2 header — `your role`
   *  when the viewer has no function role assigned. */
  layer2Label: string;
  /** Layer 3 — this status' instruction: the viewer-role override draft when
   *  non-empty, else the default-instruction draft. */
  layer3: string;
  /** Whether Layer 3 resolved to the viewer's role override or the default. */
  layer3Source: 'override' | 'default';
}

export interface UseSettingsTaskStatusesVM {
  status: SettingsTaskStatusesStatus;
  /** Active (home) org id. Null until the membership load resolves. */
  orgId: string | null;
  orgName: string | null;
  /** Viewer's permission_role.system_key in the active org. */
  viewerRole: ViewerRoleSystemKey;
  /** Owner / Admin author the instructions. Members read-only. */
  canManage: boolean;
  /** Top-level load error message (shown via a Banner). */
  error: string | null;
  /** The 6 default `task_status_instructions` rows, in fixed status order. */
  rows: TaskStatusInstruction[];
  /** The org's function roles, sorted (system canonical order, then custom
   *  alphabetical). The same list is the override slot list for every status. */
  roles: OverrideRole[];
  /** Layer 1 — the org's org-standards content (empty if none or archived). */
  orgStandards: string;
  /** The viewer's function role label, for the Layer 2 preview header. Null
   *  when the viewer has no function role assigned. */
  viewerFunctionRoleLabel: string | null;
  /** Layer 2 — the role-rule content for the viewer's function role (empty if
   *  none, archived, or the viewer has no function role). */
  viewerRoleRule: string;
  /** Which status rows are expanded. */
  expanded: Record<TaskStatusKey, boolean>;
  /** Which per-role override sub-rows are expanded, keyed status → roleId. */
  roleExpanded: Record<TaskStatusKey, Record<string, boolean>>;
  /** Working-copy drafts per status — absent when the status is clean. */
  drafts: Partial<Record<TaskStatusKey, TaskStatusDraft>>;
  /** Per-role override instruction drafts, keyed status → roleId. */
  overrideDrafts: Partial<Record<TaskStatusKey, Record<string, string>>>;
  /** The values the editor should render for a status — the draft when dirty,
   *  else the saved row. */
  editorValue: (status: TaskStatusKey) => TaskStatusDraft;
  /** True when the status has unsaved edits to its default row. */
  isDirty: (status: TaskStatusKey) => boolean;
  /** The instruction the override editor should render for (status, roleId) —
   *  the draft when present, else the saved override row, else empty. */
  overrideEditorValue: (status: TaskStatusKey, roleId: string) => string;
  /** True when (status, roleId) resolves to a non-empty override (saved or
   *  draft) — i.e. the role is `CUSTOM` rather than `using default`. */
  isOverrideCustom: (status: TaskStatusKey, roleId: string) => boolean;
  /** True when (status, roleId) has unsaved override edits. */
  isOverrideDirty: (status: TaskStatusKey, roleId: string) => boolean;
  /** True when a status has any non-empty role override (saved or draft) —
   *  drives the collapsed-row `customized` badge alongside a custom default. */
  hasAnyOverride: (status: TaskStatusKey) => boolean;
  /** True when a status has any unsaved edit — the default row or any of its
   *  per-role overrides. Drives the row's Save / Cancel enabled state. */
  isStatusDirty: (status: TaskStatusKey) => boolean;
  /** Per-status Save-in-flight flags. */
  saving: Partial<Record<TaskStatusKey, boolean>>;
  /** Per-status Save failure messages (shown via a Banner in the row body). */
  saveError: Partial<Record<TaskStatusKey, string>>;
  /** Transient "Saved." confirmation (shown via a Toast). */
  saveToast: string | null;
  /** The status whose History overlay is open, or null when it is closed. */
  historyStatus: TaskStatusKey | null;
  /** Author display handles by user id — for the History overlay `By` column. */
  handles: Record<string, string>;
  /** All `task_status_instruction_versions` for a status — the default row's
   *  snapshots interleaved with every role override's, newest-first. */
  versionsForStatus: (status: TaskStatusKey) => TaskStatusInstructionVersion[];
  /** The live (current) instruction_md a version should diff against — the
   *  saved row for the version's `(status, role)` cell, or '' when that cell
   *  has no live row (e.g. an archived override). */
  currentInstructionMd: (version: TaskStatusInstructionVersion) => string;
  /** Version id whose restore RPC is in flight, or null. */
  restoringVersionId: string | null;
  /** Version-restore failure message (shown via a Banner in the overlay). */
  restoreError: string | null;
  /** Transient restore confirmation (shown via a Toast). "Restored." on a
   *  successful restore; "Already at this version." on an `already_at_version`
   *  no-op. */
  restoreToast: string | null;
  /** The live merged Layer 1+2+3 context for a status, as an agent would see
   *  it. Layer 3 is the viewer-role override draft when non-empty, else the
   *  default-instruction draft — so the preview re-derives as editors change. */
  mergedPreview: (status: TaskStatusKey) => MergedPreview;
  actions: {
    /** Toggle a status row's expansion. Multiple rows may be open at once. */
    toggleExpand: (status: TaskStatusKey) => void;
    /** Patch the description working copy. Seeds from the saved row. */
    editDescription: (status: TaskStatusKey, value: string) => void;
    /** Patch the default-instruction working copy. Seeds from the saved row. */
    editDefaultInstruction: (status: TaskStatusKey, value: string) => void;
    /** Discard unsaved edits for a status — the default row AND every per-role
     *  override draft — reverting to the saved rows. */
    cancelEdit: (status: TaskStatusKey) => void;
    /** Persist a status row — the default cell + each dirty per-role override
     *  via `save_task_status_instruction`. A cleared override (dirty + empty)
     *  archives the saved override row via `set_task_status_instruction_archived`,
     *  reverting that role to the default. Optimistic; reverts on failure. */
    saveStatus: (status: TaskStatusKey) => Promise<void>;
    /** Dismiss the "Saved." toast. */
    clearSaveToast: () => void;
    /** Toggle a per-role override sub-row's expansion. */
    toggleRoleExpand: (status: TaskStatusKey, roleId: string) => void;
    /** Patch a per-role override instruction working copy. */
    editOverrideInstruction: (
      status: TaskStatusKey,
      roleId: string,
      value: string
    ) => void;
    /** Discard unsaved edits for a per-role override. */
    cancelOverrideEdit: (status: TaskStatusKey, roleId: string) => void;
    /** Open the History overlay for a status. */
    openHistory: (status: TaskStatusKey) => void;
    /** Close the History overlay. */
    closeHistory: () => void;
    /** Restore a `(status, role)` cell to a prior version via
     *  `restore_task_status_instruction_version`. Reconciles the live row +
     *  versions on success; an `already_at_version` no-op surfaces an
     *  "Already at this version." toast. */
    restoreVersion: (versionId: string) => Promise<void>;
    /** Dismiss the restore toast. */
    clearRestoreToast: () => void;
  };
}

interface TaskStatusInstructionVersionRow {
  id: string;
  instruction_id: string;
  status: string;
  role_id: string | null;
  version_number: number;
  description: string | null;
  instruction_md: string;
  restored_from_version_id: string | null;
  created_at: string;
  created_by: string | null;
}

interface ProfileRow {
  id: string;
  display_name: string | null;
  username: string | null;
}

function toVersion(
  r: TaskStatusInstructionVersionRow
): TaskStatusInstructionVersion {
  return {
    id: r.id,
    instructionId: r.instruction_id,
    status: r.status as TaskStatusKey,
    roleId: r.role_id,
    versionNumber: r.version_number,
    description: r.description ?? '',
    instructionMd: r.instruction_md,
    restoredFromVersionId: r.restored_from_version_id,
    createdAt: r.created_at,
    createdBy: r.created_by
  };
}

interface TaskStatusInstructionRow {
  id: string;
  org_id: string;
  status: string;
  role_id: string | null;
  description: string | null;
  instruction_md: string;
  archived_at: string | null;
  updated_at: string;
}

interface FunctionRoleRow {
  id: string;
  key: string;
  label: string;
  description: string | null;
  kind: string;
}

/** The `task_status_instructions` row shape `save_task_status_instruction` /
 *  `set_task_status_instruction_archived` return inside their `{ok:true,
 *  instruction}` envelope (`to_jsonb` of the full row). */
interface TaskStatusInstructionEnvelopeRow {
  id: string;
  org_id: string;
  status: string;
  role_id: string | null;
  description: string | null;
  instruction_md: string;
  archived_at: string | null;
  updated_at: string;
}

function envelopeToInstruction(
  r: TaskStatusInstructionEnvelopeRow
): TaskStatusInstruction {
  return {
    id: r.id,
    orgId: r.org_id,
    status: r.status as TaskStatusKey,
    roleId: r.role_id,
    description: r.description ?? '',
    instructionMd: r.instruction_md,
    archivedAt: r.archived_at,
    updatedAt: r.updated_at
  };
}

function emptyExpanded(): Record<TaskStatusKey, boolean> {
  return {
    todo: false,
    in_progress: false,
    blocked: false,
    in_review: false,
    done: false,
    cancelled: false
  };
}

function emptyRoleExpanded(): Record<TaskStatusKey, Record<string, boolean>> {
  return {
    todo: {},
    in_progress: {},
    blocked: {},
    in_review: {},
    done: {},
    cancelled: {}
  };
}

/**
 * ViewModel for `/settings/task-statuses` (turn 11.2f — the History overlay:
 * per-status version list, diff against current, and restore — on top of the
 * 11.2b–11.2e model + view shell + Edit tab + per-role overrides + merged
 * preview + Save wiring).
 *
 * Resolves the caller's home org (id + name) and viewer
 * permission_role.system_key with the same "oldest membership wins" heuristic
 * as the sibling settings VMs, then loads — in parallel — the org's
 * `function_roles` and ALL its `task_status_instructions` rows. The rows are
 * split into the 6 default rows (`role_id` NULL) and the per-role override rows
 * (`role_id` set, non-archived). The rows are seeded on org creation (turn
 * 11.2a) so there is no "empty" content state; a missing seed row is
 * synthesized from the platform defaults defensively.
 *
 * Readable by Members too — there is no `forbidden` status. The View gates the
 * editors + the overlay's Restore affordance on `canManage`; Members can open
 * the History overlay and diff versions but not restore.
 */
export function useSettingsTaskStatusesVM(): UseSettingsTaskStatusesVM {
  const [status, setStatus] =
    useState<SettingsTaskStatusesStatus>('loading');
  // The active org is owned by the shell (see `useActiveOrg`). This VM is a
  // pure consumer: it scopes every query to whatever org the shell says is
  // active and re-loads when the user switches. It must NOT re-derive its own
  // "oldest membership" org — that was the cross-org leak. The public
  // `orgId` / `orgName` fields are sourced from context.
  const { activeOrgId: orgId, activeOrgName: orgName } = useActiveOrg();
  const [viewerRole, setViewerRole] = useState<ViewerRoleSystemKey>(null);
  const [error, setError] = useState<string | null>(null);
  const [rows, setRows] = useState<TaskStatusInstruction[]>([]);
  const [overrideRows, setOverrideRows] = useState<TaskStatusInstruction[]>([]);
  const [roles, setRoles] = useState<OverrideRole[]>([]);
  const [orgStandards, setOrgStandards] = useState('');
  const [viewerRoleRule, setViewerRoleRule] = useState('');
  const [viewerFunctionRoleId, setViewerFunctionRoleId] = useState<
    string | null
  >(null);
  const [viewerFunctionRoleLabel, setViewerFunctionRoleLabel] = useState<
    string | null
  >(null);
  const [expanded, setExpanded] =
    useState<Record<TaskStatusKey, boolean>>(emptyExpanded);
  const [roleExpanded, setRoleExpanded] = useState<
    Record<TaskStatusKey, Record<string, boolean>>
  >(emptyRoleExpanded);
  const [drafts, setDrafts] = useState<
    Partial<Record<TaskStatusKey, TaskStatusDraft>>
  >({});
  const [overrideDrafts, setOverrideDrafts] = useState<
    Partial<Record<TaskStatusKey, Record<string, string>>>
  >({});
  const [saving, setSaving] = useState<
    Partial<Record<TaskStatusKey, boolean>>
  >({});
  const [saveError, setSaveError] = useState<
    Partial<Record<TaskStatusKey, string>>
  >({});
  const [saveToast, setSaveToast] = useState<string | null>(null);
  const [versions, setVersions] = useState<TaskStatusInstructionVersion[]>([]);
  const [handles, setHandles] = useState<Record<string, string>>({});
  const [historyStatus, setHistoryStatus] = useState<TaskStatusKey | null>(
    null
  );
  const [restoringVersionId, setRestoringVersionId] = useState<string | null>(
    null
  );
  const [restoreError, setRestoreError] = useState<string | null>(null);
  const [restoreToast, setRestoreToast] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data: sessionData } = await supabase.auth.getSession();
      if (cancelled) return;
      const session = sessionData.session;
      if (!session) {
        setStatus('unauth');
        return;
      }

      // No active org yet — the shell is still resolving which org to land in.
      // Stay in the loading state; this effect re-runs once it resolves.
      if (!orgId) return;
      const resolvedOrgId = orgId;

      // Viewer's membership in THIS org — for the function role (Layer 2) +
      // permission_role.system_key (canManage). Filtered by the active org,
      // not the oldest membership.
      const { data: membershipRow, error: memErr } = await supabase
        .from('memberships')
        .select(
          'function_role_id, permission_role:permission_role_id(system_key)'
        )
        .eq('user_id', session.user.id)
        .eq('org_id', resolvedOrgId)
        .maybeSingle();

      if (cancelled) return;
      if (memErr) {
        setStatus('error');
        setError("Couldn't load task statuses. Try again.");
        return;
      }

      type RoleEmbed =
        | { system_key?: string | null }
        | { system_key?: string | null }[]
        | null;
      const mem = membershipRow as {
        function_role_id: string | null;
        permission_role: RoleEmbed;
      } | null;
      const sysKey = mem
        ? Array.isArray(mem.permission_role)
          ? mem.permission_role[0]?.system_key ?? null
          : mem.permission_role?.system_key ?? null
        : null;
      const role: ViewerRoleSystemKey =
        sysKey === 'owner' || sysKey === 'admin' || sysKey === 'member'
          ? sysKey
          : null;
      const viewerFnRoleId = mem?.function_role_id ?? null;

      // Load — in parallel — the org's function_roles, ALL its
      // task_status_instructions rows (default + per-role override), the
      // singleton org_standards row (Layer 1 of the merged preview), and the
      // role_rules row for the viewer's function role (Layer 2). The last is
      // skipped when the viewer has no function role assigned.
      const [rolesRes, rowsRes, stdRes, ruleRes, versionsRes] =
        await Promise.all([
          supabase
            .from('function_roles')
            .select('id, key, label, description, kind')
            .eq('org_id', resolvedOrgId)
            .is('deleted_at', null),
          supabase
            .from('task_status_instructions')
            .select(
              'id, org_id, status, role_id, description, instruction_md, archived_at, updated_at'
            )
            .eq('org_id', resolvedOrgId),
          supabase
            .from('org_standards')
            .select('content, archived_at')
            .eq('org_id', resolvedOrgId)
            .maybeSingle(),
          viewerFnRoleId
            ? supabase
                .from('role_rules')
                .select('content, archived_at')
                .eq('org_id', resolvedOrgId)
                .eq('function_role_id', viewerFnRoleId)
                .maybeSingle()
            : Promise.resolve({
                data: null as {
                  content: string;
                  archived_at: string | null;
                } | null,
                error: null
              }),
          supabase
            .from('task_status_instruction_versions')
            .select(
              'id, instruction_id, status, role_id, version_number, description, instruction_md, restored_from_version_id, created_at, created_by'
            )
            .eq('org_id', resolvedOrgId)
            .order('created_at', { ascending: false })
        ]);

      if (cancelled) return;
      if (
        rolesRes.error ||
        rowsRes.error ||
        stdRes.error ||
        ruleRes.error ||
        versionsRes.error
      ) {
        setStatus('error');
        setError("Couldn't load task statuses. Try again.");
        return;
      }

      const roleRows = (rolesRes.data ?? []) as ReadonlyArray<FunctionRoleRow>;
      const asFunctionRoles: FunctionRole[] = roleRows.map((r) => ({
        id: r.id,
        key: r.key,
        label: r.label,
        description: r.description,
        kind: r.kind === 'custom' ? 'custom' : 'system',
        memberCount: 0,
        agentRule: emptyRule()
      }));
      const { system, custom } = sortRoles(asFunctionRoles);
      const sortedRoles: OverrideRole[] = [...system, ...custom].map((r) => ({
        id: r.id,
        key: r.key,
        label: r.label,
        kind: r.kind
      }));

      const loaded = (rowsRes.data ?? []) as ReadonlyArray<
        TaskStatusInstructionRow
      >;
      const byStatus = new Map<TaskStatusKey, TaskStatusInstruction>();
      const overrides: TaskStatusInstruction[] = [];
      for (const r of loaded) {
        if (!isTaskStatusKey(r.status)) continue;
        const inst: TaskStatusInstruction = {
          id: r.id,
          orgId: r.org_id,
          status: r.status,
          roleId: r.role_id,
          description: r.description ?? '',
          instructionMd: r.instruction_md,
          archivedAt: r.archived_at,
          updatedAt: r.updated_at
        };
        if (r.role_id === null) {
          byStatus.set(r.status, inst);
        } else if (r.archived_at === null) {
          // Archived overrides fall back to the default — ignore them here.
          overrides.push(inst);
        }
      }

      // Build the 6 default rows in canonical order — synthesize a seed row for
      // any status whose row is somehow absent.
      const resolved = TASK_STATUS_KEYS.map(
        (key) => byStatus.get(key) ?? seedRow(resolvedOrgId, key)
      );

      // Merged-preview layers. Archived org-standards / role-rules contribute
      // nothing — an archived layer is removed from every agent's context.
      const stdRow = stdRes.data as
        | { content: string; archived_at: string | null }
        | null;
      const ruleRow = ruleRes.data as
        | { content: string; archived_at: string | null }
        | null;
      const resolvedOrgStandards =
        stdRow && stdRow.archived_at === null ? stdRow.content : '';
      const resolvedRoleRule =
        ruleRow && ruleRow.archived_at === null ? ruleRow.content : '';
      const viewerRoleLabel = viewerFnRoleId
        ? sortedRoles.find((r) => r.id === viewerFnRoleId)?.label ?? null
        : null;

      const versionRows = (versionsRes.data ?? []) as ReadonlyArray<
        TaskStatusInstructionVersionRow
      >;
      const resolvedVersions = versionRows
        .filter((v) => isTaskStatusKey(v.status))
        .map(toVersion);

      // Resolve author display handles for the version rows (History `By`).
      const authorIds = Array.from(
        new Set(
          versionRows
            .map((v) => v.created_by)
            .filter((id): id is string => !!id)
        )
      );
      const handleMap: Record<string, string> = {};
      if (authorIds.length > 0) {
        const { data: profileRows } = await supabase
          .from('profiles')
          .select('id, display_name, username')
          .in('id', authorIds);
        if (cancelled) return;
        for (const p of (profileRows ?? []) as ReadonlyArray<ProfileRow>) {
          handleMap[p.id] = p.display_name || p.username || 'someone';
        }
      }

      setViewerRole(role);
      setRows(resolved);
      setOverrideRows(overrides);
      setRoles(sortedRoles);
      setOrgStandards(resolvedOrgStandards);
      setViewerRoleRule(resolvedRoleRule);
      setViewerFunctionRoleId(viewerFnRoleId);
      setViewerFunctionRoleLabel(viewerRoleLabel);
      setVersions(resolvedVersions);
      setHandles(handleMap);
      setStatus('ready');
    })();
    return () => {
      cancelled = true;
    };
  }, [orgId]);

  const rowMap = useMemo(() => {
    const m = new Map<TaskStatusKey, TaskStatusInstruction>();
    for (const r of rows) m.set(r.status, r);
    return m;
  }, [rows]);

  /** status → roleId → saved (non-archived) override row. */
  const overrideRowMap = useMemo(() => {
    const m = new Map<TaskStatusKey, Map<string, TaskStatusInstruction>>();
    for (const r of overrideRows) {
      if (r.roleId === null) continue;
      let inner = m.get(r.status);
      if (!inner) {
        inner = new Map();
        m.set(r.status, inner);
      }
      inner.set(r.roleId, r);
    }
    return m;
  }, [overrideRows]);

  const editorValue = useCallback(
    (s: TaskStatusKey): TaskStatusDraft => {
      const draft = drafts[s];
      if (draft) return draft;
      const row = rowMap.get(s);
      return {
        description: row?.description ?? '',
        instructionMd: row?.instructionMd ?? ''
      };
    },
    [drafts, rowMap]
  );

  const isDirty = useCallback(
    (s: TaskStatusKey): boolean => {
      const draft = drafts[s];
      if (!draft) return false;
      const row = rowMap.get(s);
      return (
        draft.description !== (row?.description ?? '') ||
        draft.instructionMd !== (row?.instructionMd ?? '')
      );
    },
    [drafts, rowMap]
  );

  const overrideEditorValue = useCallback(
    (s: TaskStatusKey, roleId: string): string => {
      const draft = overrideDrafts[s]?.[roleId];
      if (draft !== undefined) return draft;
      return overrideRowMap.get(s)?.get(roleId)?.instructionMd ?? '';
    },
    [overrideDrafts, overrideRowMap]
  );

  const isOverrideCustom = useCallback(
    (s: TaskStatusKey, roleId: string): boolean =>
      overrideEditorValue(s, roleId).trim().length > 0,
    [overrideEditorValue]
  );

  const isOverrideDirty = useCallback(
    (s: TaskStatusKey, roleId: string): boolean => {
      const draft = overrideDrafts[s]?.[roleId];
      if (draft === undefined) return false;
      const saved = overrideRowMap.get(s)?.get(roleId)?.instructionMd ?? '';
      return draft !== saved;
    },
    [overrideDrafts, overrideRowMap]
  );

  const hasAnyOverride = useCallback(
    (s: TaskStatusKey): boolean => {
      const inner = overrideRowMap.get(s);
      if (inner) {
        for (const r of inner.values()) {
          if (r.instructionMd.trim().length > 0) return true;
        }
      }
      const ds = overrideDrafts[s];
      if (ds) {
        for (const v of Object.values(ds)) {
          if (v.trim().length > 0) return true;
        }
      }
      return false;
    },
    [overrideRowMap, overrideDrafts]
  );

  const mergedPreview = useCallback(
    (s: TaskStatusKey): MergedPreview => {
      const override = viewerFunctionRoleId
        ? overrideEditorValue(s, viewerFunctionRoleId)
        : '';
      const useOverride = override.trim().length > 0;
      return {
        layer1: orgStandards,
        layer2: viewerRoleRule,
        layer2Label: viewerFunctionRoleLabel ?? 'your role',
        layer3: useOverride ? override : editorValue(s).instructionMd,
        layer3Source: useOverride ? 'override' : 'default'
      };
    },
    [
      viewerFunctionRoleId,
      overrideEditorValue,
      orgStandards,
      viewerRoleRule,
      viewerFunctionRoleLabel,
      editorValue
    ]
  );

  const toggleExpand = useCallback((s: TaskStatusKey) => {
    setExpanded((cur) => ({ ...cur, [s]: !cur[s] }));
  }, []);

  const clearStatusSaveError = useCallback((s: TaskStatusKey) => {
    setSaveError((cur) => {
      if (!(s in cur)) return cur;
      const next = { ...cur };
      delete next[s];
      return next;
    });
  }, []);

  const patchDraft = useCallback(
    (s: TaskStatusKey, patch: Partial<TaskStatusDraft>) => {
      setDrafts((cur) => {
        const row = rowMap.get(s);
        const base = cur[s] ?? {
          description: row?.description ?? '',
          instructionMd: row?.instructionMd ?? ''
        };
        return { ...cur, [s]: { ...base, ...patch } };
      });
      clearStatusSaveError(s);
    },
    [rowMap, clearStatusSaveError]
  );

  const editDescription = useCallback(
    (s: TaskStatusKey, value: string) => patchDraft(s, { description: value }),
    [patchDraft]
  );

  const editDefaultInstruction = useCallback(
    (s: TaskStatusKey, value: string) =>
      patchDraft(s, { instructionMd: value }),
    [patchDraft]
  );

  const cancelEdit = useCallback(
    (s: TaskStatusKey) => {
      setDrafts((cur) => {
        if (!(s in cur)) return cur;
        const next = { ...cur };
        delete next[s];
        return next;
      });
      setOverrideDrafts((cur) => {
        if (!(s in cur)) return cur;
        const next = { ...cur };
        delete next[s];
        return next;
      });
      clearStatusSaveError(s);
    },
    [clearStatusSaveError]
  );

  const toggleRoleExpand = useCallback(
    (s: TaskStatusKey, roleId: string) => {
      setRoleExpanded((cur) => ({
        ...cur,
        [s]: { ...cur[s], [roleId]: !cur[s]?.[roleId] }
      }));
    },
    []
  );

  const editOverrideInstruction = useCallback(
    (s: TaskStatusKey, roleId: string, value: string) => {
      setOverrideDrafts((cur) => ({
        ...cur,
        [s]: { ...cur[s], [roleId]: value }
      }));
      clearStatusSaveError(s);
    },
    [clearStatusSaveError]
  );

  const cancelOverrideEdit = useCallback(
    (s: TaskStatusKey, roleId: string) => {
      setOverrideDrafts((cur) => {
        const inner = cur[s];
        if (!inner || !(roleId in inner)) return cur;
        const nextInner = { ...inner };
        delete nextInner[roleId];
        return { ...cur, [s]: nextInner };
      });
    },
    []
  );

  const isStatusDirty = useCallback(
    (s: TaskStatusKey): boolean => {
      if (isDirty(s)) return true;
      for (const r of roles) {
        if (isOverrideDirty(s, r.id)) return true;
      }
      return false;
    },
    [isDirty, isOverrideDirty, roles]
  );

  const clearSaveToast = useCallback(() => setSaveToast(null), []);
  const clearRestoreToast = useCallback(() => setRestoreToast(null), []);

  const versionsForStatus = useCallback(
    (s: TaskStatusKey): TaskStatusInstructionVersion[] =>
      sortTaskStatusVersions(versions.filter((v) => v.status === s)),
    [versions]
  );

  const currentInstructionMd = useCallback(
    (version: TaskStatusInstructionVersion): string => {
      if (version.roleId === null) {
        return rowMap.get(version.status)?.instructionMd ?? '';
      }
      return (
        overrideRowMap.get(version.status)?.get(version.roleId)
          ?.instructionMd ?? ''
      );
    },
    [rowMap, overrideRowMap]
  );

  const openHistory = useCallback((s: TaskStatusKey) => {
    setRestoreError(null);
    setHistoryStatus(s);
  }, []);

  const closeHistory = useCallback(() => {
    setRestoreError(null);
    setHistoryStatus(null);
  }, []);

  /** Re-fetch `task_status_instruction_versions` for the org after a restore so
   *  the History overlay stays current, then resolve any newly-seen authors.
   *  Best-effort — a failure leaves the prior list. */
  const reloadVersions = useCallback(async () => {
    if (!orgId) return;
    const { data, error: vErr } = await supabase
      .from('task_status_instruction_versions')
      .select(
        'id, instruction_id, status, role_id, version_number, description, instruction_md, restored_from_version_id, created_at, created_by'
      )
      .eq('org_id', orgId)
      .order('created_at', { ascending: false });
    if (vErr || !data) return;
    const rows = data as ReadonlyArray<TaskStatusInstructionVersionRow>;
    setVersions(rows.filter((v) => isTaskStatusKey(v.status)).map(toVersion));

    const seen = Array.from(
      new Set(
        rows.map((v) => v.created_by).filter((id): id is string => !!id)
      )
    );
    setHandles((cur) => {
      const missing = seen.filter((id) => !(id in cur));
      if (missing.length === 0) return cur;
      void (async () => {
        const { data: profileRows } = await supabase
          .from('profiles')
          .select('id, display_name, username')
          .in('id', missing);
        if (!profileRows) return;
        setHandles((prev) => {
          const next = { ...prev };
          for (const p of profileRows as ReadonlyArray<ProfileRow>) {
            next[p.id] = p.display_name || p.username || 'someone';
          }
          return next;
        });
      })();
      return cur;
    });
  }, [orgId]);

  /**
   * Restore a `(status, role)` cell to a prior version via
   * `restore_task_status_instruction_version`. The RPC snapshots the current
   * state, applies the source snapshot's content, and appends a second
   * snapshot referencing the source. On success the live row is reconciled
   * from the returned `task_status_instructions` row (default → `rows`,
   * override → `overrideRows`), any working copy for the status is dropped, and
   * the version list is refreshed. An `already_at_version` no-op surfaces an
   * "Already at this version." toast.
   */
  const restoreVersion = useCallback(
    async (versionId: string) => {
      setRestoreError(null);
      setRestoringVersionId(versionId);

      const { data, error: rpcError } = await supabase.rpc(
        'restore_task_status_instruction_version',
        { p_version_id: versionId }
      );
      setRestoringVersionId(null);

      const envelope = data as
        | { ok: true; instruction: TaskStatusInstructionEnvelopeRow }
        | { ok: false; error: string }
        | null;
      if (rpcError || !envelope) {
        setRestoreError("Couldn't restore the version. Try again.");
        return;
      }
      if (envelope.ok !== true) {
        if (envelope.error === 'already_at_version') {
          setRestoreToast('Already at this version.');
          return;
        }
        setRestoreError("Couldn't restore the version. Try again.");
        return;
      }

      const restored = envelopeToInstruction(envelope.instruction);
      const s = restored.status;
      if (restored.roleId === null) {
        setRows((cur) =>
          cur.map((row) => (row.status === s ? restored : row))
        );
        setDrafts((cur) => {
          if (!(s in cur)) return cur;
          const next = { ...cur };
          delete next[s];
          return next;
        });
      } else {
        const roleId = restored.roleId;
        setOverrideRows((cur) => {
          const without = cur.filter(
            (r) => !(r.status === s && r.roleId === roleId)
          );
          return restored.archivedAt === null
            ? [...without, restored]
            : without;
        });
        setOverrideDrafts((cur) => {
          const inner = cur[s];
          if (!inner || !(roleId in inner)) return cur;
          const nextInner = { ...inner };
          delete nextInner[roleId];
          return { ...cur, [s]: nextInner };
        });
      }
      setRestoreToast('Restored.');
      void reloadVersions();
    },
    [reloadVersions]
  );

  /**
   * Persist a status row: the default cell + each dirty per-role override.
   *
   * - The default cell + each dirty override with non-empty text →
   *   `save_task_status_instruction` (the default cell with `p_role_id` NULL).
   * - A dirty override whose draft is now empty AND has a saved row →
   *   `set_task_status_instruction_archived` (archives the override row;
   *   the role falls back to the status default, the row stays in history).
   * - A dirty override whose draft is empty and was never saved → nothing to
   *   persist; the draft is simply dropped.
   *
   * Optimistic: the working copies are applied to the live rows immediately and
   * the drafts cleared; every RPC fires in parallel. If any fails the live rows
   * + drafts are reverted from the pre-save snapshot and a per-status error is
   * surfaced. On success the rows are reconciled with the authoritative rows
   * the RPCs returned and a "Saved." toast fires.
   */
  const saveStatus = useCallback(
    async (s: TaskStatusKey) => {
      if (!orgId) return;
      const defaultDirty = isDirty(s);
      const dirtyRoles = roles.filter((r) => isOverrideDirty(s, r.id));
      if (!defaultDirty && dirtyRoles.length === 0) return;

      // Snapshots for revert.
      const rowsSnapshot = rows;
      const overrideRowsSnapshot = overrideRows;
      const draftSnapshot = drafts[s];
      const overrideDraftSnapshot = overrideDrafts[s];
      const defaultDraft = editorValue(s);

      type Op =
        | { kind: 'default' }
        | { kind: 'override'; roleId: string; value: string }
        | { kind: 'clear'; roleId: string; instructionId: string };
      const ops: Op[] = [];
      if (defaultDirty) ops.push({ kind: 'default' });
      for (const r of dirtyRoles) {
        const value = overrideEditorValue(s, r.id);
        if (value.trim().length > 0) {
          ops.push({ kind: 'override', roleId: r.id, value });
        } else {
          const savedRow = overrideRowMap.get(s)?.get(r.id);
          if (savedRow) {
            ops.push({
              kind: 'clear',
              roleId: r.id,
              instructionId: savedRow.id
            });
          }
        }
      }
      if (ops.length === 0) {
        // Every dirty override was an empty draft that was never saved —
        // nothing to persist, just drop the drafts.
        cancelEdit(s);
        return;
      }

      clearStatusSaveError(s);
      setSaving((cur) => ({ ...cur, [s]: true }));

      // Optimistic apply.
      if (defaultDirty) {
        setRows((cur) =>
          cur.map((row) =>
            row.status === s
              ? {
                  ...row,
                  description: defaultDraft.description,
                  instructionMd: defaultDraft.instructionMd
                }
              : row
          )
        );
      }
      setOverrideRows((cur) => {
        let next = cur;
        for (const op of ops) {
          if (op.kind === 'override') {
            next = next.filter(
              (r) => !(r.status === s && r.roleId === op.roleId)
            );
            next = [
              ...next,
              {
                id: `optimistic-${s}-${op.roleId}`,
                orgId,
                status: s,
                roleId: op.roleId,
                description: '',
                instructionMd: op.value,
                archivedAt: null,
                updatedAt: new Date().toISOString()
              }
            ];
          } else if (op.kind === 'clear') {
            next = next.filter(
              (r) => !(r.status === s && r.roleId === op.roleId)
            );
          }
        }
        return next;
      });
      setDrafts((cur) => {
        if (!(s in cur)) return cur;
        const next = { ...cur };
        delete next[s];
        return next;
      });
      setOverrideDrafts((cur) => {
        if (!(s in cur)) return cur;
        const next = { ...cur };
        delete next[s];
        return next;
      });

      const results = await Promise.all(
        ops.map((op) => {
          if (op.kind === 'default') {
            return supabase.rpc('save_task_status_instruction', {
              p_org_id: orgId,
              p_status: s,
              // The default cell is keyed role_id NULL — the generated RPC
              // type narrows p_role_id to string, but the SQL accepts NULL.
              p_role_id: null as unknown as string,
              p_description: defaultDraft.description,
              p_instruction_md: defaultDraft.instructionMd
            });
          }
          if (op.kind === 'override') {
            return supabase.rpc('save_task_status_instruction', {
              p_org_id: orgId,
              p_status: s,
              p_role_id: op.roleId,
              p_description: '',
              p_instruction_md: op.value
            });
          }
          return supabase.rpc('set_task_status_instruction_archived', {
            p_instruction_id: op.instructionId,
            p_archived: true
          });
        })
      );

      setSaving((cur) => {
        const next = { ...cur };
        delete next[s];
        return next;
      });

      let failure: string | null = null;
      for (const { data, error: rpcErr } of results) {
        const envelope = data as
          | { ok?: boolean; error?: string }
          | null;
        if (rpcErr || !envelope || envelope.ok !== true) {
          if (envelope && envelope.error === 'description_too_long') {
            failure = 'Description too long (max 200).';
          } else if (!failure) {
            failure = "Couldn't save. Try again.";
          }
        }
      }

      if (failure) {
        setRows(rowsSnapshot);
        setOverrideRows(overrideRowsSnapshot);
        if (draftSnapshot) {
          setDrafts((cur) => ({ ...cur, [s]: draftSnapshot }));
        }
        if (overrideDraftSnapshot) {
          setOverrideDrafts((cur) => ({
            ...cur,
            [s]: overrideDraftSnapshot
          }));
        }
        setSaveError((cur) => ({ ...cur, [s]: failure as string }));
        return;
      }

      // Reconcile with the authoritative rows the RPCs returned.
      setRows((cur) =>
        cur.map((row) => {
          if (row.status !== s) return row;
          const i = ops.findIndex((o) => o.kind === 'default');
          if (i < 0) return row;
          const env = results[i].data as {
            ok: true;
            instruction: TaskStatusInstructionEnvelopeRow;
          };
          return envelopeToInstruction(env.instruction);
        })
      );
      setOverrideRows((cur) => {
        let next = cur;
        for (let i = 0; i < ops.length; i++) {
          const op = ops[i];
          if (op.kind === 'override') {
            next = next.filter(
              (r) => !(r.status === s && r.roleId === op.roleId)
            );
            const env = results[i].data as {
              ok: true;
              instruction: TaskStatusInstructionEnvelopeRow;
            };
            next = [...next, envelopeToInstruction(env.instruction)];
          } else if (op.kind === 'clear') {
            next = next.filter(
              (r) => !(r.status === s && r.roleId === op.roleId)
            );
          }
        }
        return next;
      });
      setSaveToast('Saved.');
    },
    [
      orgId,
      rows,
      overrideRows,
      drafts,
      overrideDrafts,
      roles,
      isDirty,
      isOverrideDirty,
      editorValue,
      overrideEditorValue,
      overrideRowMap,
      cancelEdit,
      clearStatusSaveError
    ]
  );

  const canManage = viewerRole === 'owner' || viewerRole === 'admin';

  return {
    status,
    orgId,
    orgName,
    viewerRole,
    canManage,
    error,
    rows,
    roles,
    orgStandards,
    viewerFunctionRoleLabel,
    viewerRoleRule,
    expanded,
    roleExpanded,
    drafts,
    overrideDrafts,
    editorValue,
    isDirty,
    overrideEditorValue,
    isOverrideCustom,
    isOverrideDirty,
    hasAnyOverride,
    isStatusDirty,
    saving,
    saveError,
    saveToast,
    historyStatus,
    handles,
    versionsForStatus,
    currentInstructionMd,
    restoringVersionId,
    restoreError,
    restoreToast,
    mergedPreview,
    actions: {
      toggleExpand,
      editDescription,
      editDefaultInstruction,
      cancelEdit,
      toggleRoleExpand,
      editOverrideInstruction,
      cancelOverrideEdit,
      saveStatus,
      clearSaveToast,
      openHistory,
      closeHistory,
      restoreVersion,
      clearRestoreToast
    }
  };
}
